import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { cataloghiAPI, type Catalogo } from '../utils/cataloghiAPI';
import { usePermissions } from '../hooks/usePermissions';
import AllegatoPreviewModal from '../components/AllegatoPreviewModal';

const INPUT = 'mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm';
interface CatalogForm {
  id?: string;
  marca: string;
  titolo: string;
  tipo: 'file' | 'link';
  url: string;
}

function errorMessage(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
  return typeof detail === 'string' ? detail : 'Operazione non riuscita. Verifica i dati e riprova.';
}

function CatalogDialog({ label, onClose, children }: {
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const content = useRef<HTMLDivElement>(null);
  const dismiss = useRef(onClose);
  dismiss.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const focusable = () => Array.from(content.current?.querySelectorAll<HTMLElement>(
      'input:not([disabled]), select:not([disabled]), button:not([disabled]), a[href]',
    ) || []);
    focusable()[0]?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); dismiss.current(); }
      if (event.key !== 'Tab') return;
      const elements = focusable();
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first?.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); previous?.focus(); };
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div ref={content} role="dialog" aria-modal="true" aria-label={label}
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg bg-white p-6">
        {children}
      </div>
    </div>
  );
}

export default function Cataloghi() {
  const { hasPermission } = usePermissions();
  const client = useQueryClient();
  const [marca, setMarca] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [form, setForm] = useState<CatalogForm | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<Catalogo | null>(null);
  const [preview, setPreview] = useState<{ item: Catalogo; url: string } | null>(null);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['cataloghi', page, marca, search],
    queryFn: async () => (await cataloghiAPI.list({
      page, marca: marca || undefined, search: search || undefined,
    })).data,
  });
  const { data: brands } = useQuery({
    queryKey: ['cataloghi', 'marche'], queryFn: async () => (await cataloghiAPI.brands()).data,
  });
  const refresh = () => client.invalidateQueries({ queryKey: ['cataloghi'] });

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!form || busy) return;
    const data = {
      marca: form.marca.trim(), titolo: form.titolo.trim(),
      ...(form.tipo === 'link' ? { url: form.url.trim() } : {}),
    };
    if (!data.marca || !data.titolo) { toast.error('Inserisci marca e titolo'); return; }
    if (!form.id && form.tipo === 'file' && !file) { toast.error('Seleziona un documento'); return; }
    if (file && file.size > 25 * 1024 * 1024) {
      toast.error('Il documento deve essere al massimo 25 MB'); return;
    }
    setBusy(true);
    try {
      if (form.id) await cataloghiAPI.update(form.id, data);
      else if (form.tipo === 'link') await cataloghiAPI.createLink(data);
      else await cataloghiAPI.upload(data, file!);
      setForm(null); setFile(null);
      await refresh(); toast.success('Catalogo salvato');
    } catch (error) { toast.error(errorMessage(error)); }
    finally { setBusy(false); }
  };

  const openFile = async (item: Catalogo, download: boolean) => {
    // Keep the user gesture while waiting for the short-lived download URL.
    const tab = download ? window.open('about:blank', '_blank') : null;
    if (tab) tab.opener = null;
    try {
      const { data } = await cataloghiAPI.url(item.id, download);
      if (download) {
        if (tab) tab.location.replace(data.url);
        else toast.error('Consenti le finestre popup per scaricare il documento');
      } else setPreview({ item, url: data.url });
    } catch (error) { tab?.close(); toast.error(errorMessage(error)); }
  };

  const remove = async () => {
    if (!deleting || busy) return;
    setBusy(true);
    try {
      await cataloghiAPI.delete(deleting.id);
      setDeleting(null); setPage(1);
      await refresh(); toast.success('Catalogo eliminato');
    } catch (error) { toast.error(errorMessage(error)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Cataloghi per marca</h1>
          <p className="text-sm text-gray-500">Documenti e link dei produttori</p>
        </div>
        {hasPermission('cataloghi:create') && (
          <button className="rounded-lg bg-blue-600 px-4 py-2 text-white" onClick={() => {
            setFile(null); setForm({ marca, titolo: '', tipo: 'file', url: '' });
          }}>Aggiungi catalogo</button>
        )}
      </div>
      <div className="grid gap-3 rounded-lg bg-white p-4 shadow sm:grid-cols-2">
        <label className="text-sm">Marca
          <select className={INPUT} value={marca} onChange={(event) => {
            setMarca(event.target.value); setPage(1);
          }}>
            <option value="">Tutte le marche</option>
            {brands?.map((name) => <option key={name}>{name}</option>)}
          </select>
        </label>
        <label className="text-sm">Cerca catalogo
          <input className={INPUT} value={search} onChange={(event) => {
            setSearch(event.target.value); setPage(1);
          }} placeholder="Marca o titolo" />
        </label>
      </div>
      {isLoading ? <p role="status">Caricamento cataloghi…</p> : isError ? (
        <p role="alert" className="text-red-600">Impossibile caricare i cataloghi.</p>
      ) : (
        <div className="space-y-3">
          {!data?.data.length && <p className="rounded-lg bg-white p-6 text-gray-500">Nessun catalogo trovato.</p>}
          {data?.data.map((item) => (
            <article key={item.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white p-4 shadow">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500">{item.marca}</p>
                <h2 className="font-semibold break-words">{item.titolo}</h2>
                <p className="text-xs text-gray-500 break-all">
                  {item.tipo === 'link' ? item.url : `${item.filename}${item.file_size_bytes ? ` · ${(item.file_size_bytes / 1024 / 1024).toFixed(1)} MB` : ''}`}
                </p>
              </div>
              <div className="flex flex-wrap gap-3 text-sm">
                {item.tipo === 'link' ? (
                  <a className="text-blue-700" href={item.url || undefined} target="_blank" rel="noopener noreferrer">Apri link</a>
                ) : <>
                  <button className="text-blue-700" onClick={() => openFile(item, false)}>Anteprima</button>
                  <button className="text-blue-700" onClick={() => openFile(item, true)}>Scarica</button>
                </>}
                {hasPermission('cataloghi:edit') && <button onClick={() => {
                  setFile(null); setForm({ id: item.id, marca: item.marca, titolo: item.titolo, tipo: item.tipo, url: item.url || '' });
                }}>Modifica</button>}
                {hasPermission('cataloghi:delete') && <button className="text-red-600" onClick={() => setDeleting(item)}>Elimina</button>}
              </div>
            </article>
          ))}
        </div>
      )}
      {data && data.pagination.pages > 1 && (
        <div className="flex justify-end gap-3 text-sm">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>Precedente</button>
          <span>Pagina {page} di {data.pagination.pages}</span>
          <button disabled={page >= data.pagination.pages} onClick={() => setPage(page + 1)}>Successiva</button>
        </div>
      )}
      {form && (
        <CatalogDialog label={form.id ? 'Modifica catalogo' : 'Nuovo catalogo'} onClose={() => { if (!busy) setForm(null); }}>
          <form onSubmit={save} className="space-y-4">
            <h2 className="text-lg font-semibold">{form.id ? 'Modifica catalogo' : 'Nuovo catalogo'}</h2>
            <label className="block text-sm">Marca
              <input list="cataloghi-marche" required maxLength={255} className={INPUT} value={form.marca}
                onChange={(event) => setForm({ ...form, marca: event.target.value })} />
              <datalist id="cataloghi-marche">{brands?.map((name) => <option key={name} value={name} />)}</datalist>
            </label>
            <label className="block text-sm">Titolo
              <input required maxLength={255} className={INPUT} value={form.titolo}
                onChange={(event) => setForm({ ...form, titolo: event.target.value })} />
            </label>
            {!form.id && <label className="block text-sm">Tipo
              <select className={INPUT} value={form.tipo} onChange={(event) => {
                setFile(null); setForm({ ...form, tipo: event.target.value as 'file' | 'link' });
              }}>
                <option value="file">Documento</option><option value="link">Link</option>
              </select>
            </label>}
            {form.tipo === 'link' ? (
              <label className="block text-sm">Link HTTP o HTTPS
                <input key="link-url" type="url" pattern="https?://.*" required className={INPUT} value={form.url}
                  onChange={(event) => setForm({ ...form, url: event.target.value })} placeholder="https://" />
              </label>
            ) : !form.id && <label className="block text-sm">Documento (max 25 MB)
              <input key="catalog-file" type="file" required
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.heic,.heif,.csv,.txt"
                className={INPUT} onChange={(event) => setFile(event.target.files?.[0] || null)} />
            </label>}
            <div className="flex justify-end gap-3">
              <button type="button" disabled={busy} onClick={() => setForm(null)}>Annulla</button>
              <button disabled={busy} className="rounded-lg bg-blue-600 px-4 py-2 text-white">{busy ? 'Salvataggio…' : 'Salva'}</button>
            </div>
          </form>
        </CatalogDialog>
      )}
      {deleting && (
        <CatalogDialog label="Elimina catalogo" onClose={() => { if (!busy) setDeleting(null); }}>
          <div className="space-y-4">
            <h2 className="font-semibold">Eliminare «{deleting.titolo}»?</h2>
            <p className="text-sm text-gray-600">Il catalogo non sarà più disponibile nell’elenco.</p>
            <div className="flex justify-end gap-3">
              <button disabled={busy} onClick={() => setDeleting(null)}>Annulla</button>
              <button disabled={busy} className="text-red-600" onClick={remove}>Elimina</button>
            </div>
          </div>
        </CatalogDialog>
      )}
      {preview && <AllegatoPreviewModal presignedUrl={preview.url}
        filename={preview.item.filename || preview.item.titolo} contentType={preview.item.content_type}
        onClose={() => setPreview(null)} />}
    </div>
  );
}
